import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, ClipboardList, Copy, Coins, Edit3, FileDown, FileText, Files,
  Image as ImageIcon, Link2, ListChecks, Mail, Paperclip, Pencil, Pin, PinOff, Plus, Save, Search,
  Settings, Share2, StickyNote, Timer, Trash2, TrendingUp, Upload, Users, X, XCircle,
} from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import IncomeFormModal from "../components/IncomeFormModal";
import ExpenseFormModal from "../components/ExpenseFormModal";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });
const moneyShort = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });

const TABS = [
  { id: "overview", label: "Resumen", icon: ClipboardList },
  { id: "financials", label: "Financials", icon: Coins },
  { id: "notes", label: "Notas", icon: StickyNote },
  { id: "files", label: "Archivos", icon: Files },
  { id: "timeline", label: "Timeline", icon: Timer },
  { id: "settings", label: "Ajustes", icon: Settings },
];

const STATUS_META = {
  active: { label: "Activo", tone: "active" },
  pending: { label: "Pendiente", tone: "pending" },
  paused: { label: "En pausa", tone: "on_hold" },
  completed: { label: "Completado", tone: "completed" },
  archived: { label: "Archivado", tone: "archived" },
};

const FILE_CATEGORIES = [
  { key: "contract", label: "Contrato" },
  { key: "invoice", label: "Factura" },
  { key: "receipt", label: "Recibo" },
  { key: "quote", label: "Cotización" },
  { key: "photo", label: "Foto" },
  { key: "agreement", label: "Acuerdo" },
  { key: "blueprint", label: "Plano" },
  { key: "other", label: "Otro" },
];

const bytesTo = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const isImage = (ext) => ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif"].includes((ext || "").toLowerCase());

export default function ProjectDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const initialTab = new URLSearchParams(location.search).get("tab") || "overview";
  const [tab, setTab] = useState(TABS.find((t) => t.id === initialTab) ? initialTab : "overview");
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const loadCore = () => api.get(`/projects/${id}`, { withCredentials: true }).then((r) => setProject(r.data));
  const loadTx = () => Promise.all([
    api.get(`/projects/${id}/incomes`, { withCredentials: true }).then((r) => setIncomes(r.data || [])),
    api.get(`/projects/${id}/expenses`, { withCredentials: true }).then((r) => setExpenses(r.data || [])),
  ]);
  const loadNotes = () => api.get(`/projects/${id}/notes`, { withCredentials: true }).then((r) => setNotes(r.data || []));
  const loadFiles = () => api.get(`/projects/${id}/files`, { withCredentials: true }).then((r) => setFiles(r.data || []));
  const loadTimeline = () => api.get(`/projects/${id}/timeline`, { withCredentials: true }).then((r) => setTimeline(r.data || []));

  const loadAll = async () => {
    try {
      await Promise.all([loadCore(), loadTx(), loadNotes(), loadFiles(), loadTimeline()]);
    } catch (e) { setError(e.response?.data?.detail || "No pudimos cargar el proyecto."); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") && params.get("tab") !== tab) setTab(params.get("tab"));
    // eslint-disable-next-line
  }, [location.search]);

  const openClose = () => {
    api.get(`/projects/${id}/close-checklist`, { withCredentials: true })
      .then((r) => { setChecklist(r.data); setModal("close"); });
  };

  const openCreateIncome = () => { setEditingIncome(null); setModal("income"); };
  const openEditIncome = (inc) => { setEditingIncome(inc); setModal("income"); };
  const openCreateExpense = () => { setEditingExpense(null); setModal("expense"); };
  const openEditExpense = (exp) => { setEditingExpense(exp); setModal("expense"); };

  const onTxSaved = async () => {
    await Promise.all([loadCore(), loadTx(), loadTimeline()]);
  };

  const deleteTx = async (tx, kind) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este registro?")) return;
    try {
      const endpoint = kind === "income" ? `/incomes/${tx.id}` : `/expenses/${tx.id}`;
      await api.delete(endpoint, { withCredentials: true });
      await Promise.all([loadCore(), loadTx(), loadTimeline()]);
    } catch (e) { setError(e.response?.data?.detail || "No pudimos eliminar."); }
  };

  const deleteProject = async (cascade) => {
    try {
      await api.delete(`/projects/${id}${cascade ? "?cascade=true" : ""}`, { withCredentials: true });
      navigate("/proyectos");
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos eliminar el proyecto.");
    }
  };

  const submitNote = async () => {
    try {
      const payload = { ...form, tags: (form.tagsRaw || "").split(",").map((t) => t.trim()).filter(Boolean) };
      delete payload.tagsRaw;
      if (form.id) {
        await api.put(`/projects/${id}/notes/${form.id}`, payload, { withCredentials: true });
      } else {
        await api.post(`/projects/${id}/notes`, payload, { withCredentials: true });
      }
      setModal(null); setForm({});
      await Promise.all([loadNotes(), loadTimeline()]);
    } catch (e) { setError(e.response?.data?.detail || "No pudimos guardar la nota."); }
  };

  const togglePin = async (n) => {
    try {
      await api.put(`/projects/${id}/notes/${n.id}`, { pinned: !n.pinned }, { withCredentials: true });
      loadNotes();
    } catch (e) { setError(e.response?.data?.detail || "Error al fijar."); }
  };

  const deleteNote = async (n) => {
    if (!window.confirm(`¿Eliminar la nota "${n.title}"?`)) return;
    await api.delete(`/projects/${id}/notes/${n.id}`, { withCredentials: true });
    loadNotes();
  };

  const uploadFiles = async (fileList, category = "other") => {
    setError(""); setOk("");
    const ALLOWED_EXTS = new Set(["pdf", "png", "jpg", "jpeg"]);
    const ALLOWED_TYPES = new Set(["application/pdf", "image/png", "image/jpeg", "image/jpg"]);
    const picked = Array.from(fileList);
    const rejected = picked.filter((f) => {
      const ext = (f.name || "").split(".").pop().toLowerCase();
      const ct = (f.type || "").toLowerCase();
      return !ALLOWED_EXTS.has(ext) && !ALLOWED_TYPES.has(ct);
    });
    if (rejected.length) {
      setError(`Solo se permiten PDF, PNG o JPG. Descartamos: ${rejected.map((f) => f.name).join(", ")}`);
    }
    const accepted = picked.filter((f) => !rejected.includes(f));
    for (const f of accepted) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      fd.append("name", f.name);
      try {
        await api.post(`/projects/${id}/files`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) {
        setError(e.response?.data?.detail || `No pudimos subir ${f.name}.`);
      }
    }
    if (accepted.length) setOk(`${accepted.length} archivo(s) subido(s).`);
    await Promise.all([loadFiles(), loadTimeline()]);
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`¿Eliminar el archivo "${f.name}"?`)) return;
    await api.delete(`/projects/${id}/files/${f.id}`, { withCredentials: true });
    loadFiles();
  };

  const renameFile = async (f) => {
    const name = window.prompt("Nuevo nombre:", f.name);
    if (!name || name === f.name) return;
    await api.put(`/projects/${id}/files/${f.id}`, { name }, { withCredentials: true });
    loadFiles();
  };

  const submitCollab = async () => {
    try {
      await api.post(`/projects/${id}/collaborators`, form, { withCredentials: true });
      setModal(null); setForm({}); await loadCore();
    } catch (e) { setError(e.response?.data?.detail || "No pudimos invitar."); }
  };

  const finalizeClose = async () => {
    await api.put(`/projects/${id}`, { status: "completed" }, { withCredentials: true });
    setModal(null); await Promise.all([loadCore(), loadTimeline()]);
  };

  if (!project) {
    return (
      <AppShell>
        <main className="page pd-shell" data-testid="project-detail-loading">
          <div className="skeleton h1" />
          <div className="skeleton h2" />
          <div className="pd-kpi-grid">{[1,2,3,4].map((k) => <div key={k} className="skeleton" style={{ height: 84 }} />)}</div>
          <div className="skeleton" style={{ height: 320 }} />
        </main>
      </AppShell>
    );
  }

  const meta = STATUS_META[project.status] || STATUS_META.active;
  const k = project.kpis || {};
  const remainingCls = k.remaining_status === "paid" ? "paid" : k.remaining_status === "pending" ? "pending" : k.remaining_status === "partial" ? "partial" : "";
  const accent = project.color || "#4169e1";

  return (
    <AppShell>
      <main className="page pd-shell" data-testid={`project-detail-${id}`}>
        <header className="pd-header" style={{ "--accent": accent }}>
          <Link to="/proyectos" className="pd-back"><ArrowLeft size={13} /> Proyectos</Link>
          <div className="top-row">
            <div>
              <h1 className="pd-title">{project.name}</h1>
              <p className="pd-subtitle">{project.client_name || "Sin cliente"}{project.category ? ` · ${project.category}` : ""}</p>
              {project.description && <p style={{ color: "#94a3b8", marginTop: 8, maxWidth: 640, fontSize: 14 }}>{project.description}</p>}
            </div>
            <div className="pd-actions">
              <span className={`status-badge ${meta.tone}`} style={{ padding: "6px 14px", fontSize: 11 }}>{meta.label}</span>
              <button onClick={openCreateIncome} className="cash-btn primary" data-testid="project-add-income"><Plus size={14} /> Ingreso</button>
              <button onClick={openCreateExpense} className="cash-btn ghost" data-testid="project-add-expense"><Plus size={14} /> Egreso</button>
              <button onClick={() => { setForm({}); setModal("note"); }} className="cash-btn ghost" data-testid="project-add-note"><StickyNote size={14} /> Nota</button>
              <button onClick={() => { setTab("settings"); navigate(`/proyectos/${id}?tab=settings`, { replace: true }); }} className="cash-btn ghost" data-testid="project-edit-btn" title="Editar proyecto"><Edit3 size={14} /> Editar</button>
              <a
                href={`${api.defaults.baseURL}/exports/projects/${project.id}/pdf`}
                target="_blank" rel="noreferrer"
                className="cash-btn ghost"
                data-testid="project-download-pdf"
                title="Descargar reporte PDF"
              ><FileDown size={14} /> PDF</a>
              <button
                onClick={() => setModal("share")}
                className="cash-btn ghost"
                data-testid="project-share-btn"
                title="Compartir con cliente"
              ><Share2 size={14} /> Compartir</button>
              {project.status !== "completed" && (
                <button onClick={openClose} className="cash-btn ghost" style={{ color: "#10b981" }} data-testid="project-close-btn"><CheckCircle2 size={14} /> Cerrar</button>
              )}
              <button onClick={() => setModal("delete")} className="cash-btn ghost" style={{ color: "#ef4444" }} data-testid="project-delete-btn" title="Eliminar proyecto"><Trash2 size={14} /> Eliminar</button>
            </div>
          </div>
          <div className="pd-header-strip">
            <div className="item"><small>Contrato</small><b>{money.format(k.contract_value || 0)}</b></div>
            <div className="item"><small>Cobrado</small><b style={{ color: "#10b981" }}>{money.format(k.collected || 0)}</b></div>
            <div className="item"><small>Por cobrar</small><b style={{ color: remainingCls === "paid" ? "#10b981" : remainingCls === "pending" ? "#ef4444" : "#f59e0b" }}>{money.format(k.remaining || 0)}</b></div>
            <div className="item"><small>Avance</small><b>{k.progress_pct || 0}%</b></div>
            <div className="item"><small>Utilidad</small><b style={{ color: (k.profit || 0) >= 0 ? "#10b981" : "#ef4444" }}>{money.format(k.profit || 0)}</b></div>
          </div>
        </header>

        <nav className="pd-tabs" data-testid="project-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)} data-testid={`project-tab-${t.id}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </nav>

        {error && <p className="expenses-error" data-testid="pd-error">{error}</p>}
        {ok && <p style={{ background: "#dcfce7", color: "#166534", padding: 10, borderRadius: 10 }}>{ok}</p>}

        {tab === "overview" && <OverviewTab k={k} project={project} incomes={incomes} expenses={expenses} notes={notes} files={files} timeline={timeline} />}

        {tab === "financials" && (
          <FinancialsTab
            incomes={incomes}
            expenses={expenses}
            onAddIncome={openCreateIncome}
            onAddExpense={openCreateExpense}
            onEditIncome={openEditIncome}
            onEditExpense={openEditExpense}
            onDeleteIncome={(inc) => deleteTx(inc, "income")}
            onDeleteExpense={(exp) => deleteTx(exp, "expense")}
          />
        )}

        {tab === "notes" && (
          <NotesTab notes={notes} onAdd={() => { setForm({}); setModal("note"); }} onEdit={(n) => { setForm({ ...n, tagsRaw: (n.tags || []).join(", ") }); setModal("note"); }} onTogglePin={togglePin} onDelete={deleteNote} />
        )}

        {tab === "files" && (
          <FilesTab files={files} onUpload={uploadFiles} onDelete={deleteFile} onRename={renameFile} onOpenImage={setLightbox} />
        )}

        {tab === "timeline" && <TimelineTab events={timeline} />}

        {tab === "settings" && (
          <SettingsTab project={project} onSaved={loadCore} onInvite={() => { setForm({ role: "collaborator" }); setModal("collab"); }} />
        )}

        {modal === "income" && (
          <IncomeFormModal
            open
            onClose={() => { setModal(null); setEditingIncome(null); }}
            onSaved={onTxSaved}
            editingIncome={editingIncome}
            projectId={id}
            testidScope="project-income"
          />
        )}
        {modal === "expense" && (
          <ExpenseFormModal
            open
            onClose={() => { setModal(null); setEditingExpense(null); }}
            onSaved={onTxSaved}
            editingExpense={editingExpense}
            projectId={id}
            testidScope="project-expense"
          />
        )}
        {modal === "note" && (
          <NoteModal form={form} setForm={setForm} onClose={() => setModal(null)} onSubmit={submitNote} />
        )}
        {modal === "collab" && (
          <CollabModal form={form} setForm={setForm} onClose={() => setModal(null)} onSubmit={submitCollab} />
        )}
        {modal === "close" && checklist && (
          <CloseModal checklist={checklist} onClose={() => setModal(null)} onFinalize={finalizeClose} />
        )}
        {modal === "share" && (
          <ShareModal projectId={id} project={project} onClose={() => setModal(null)} onChanged={loadCore} />
        )}
        {modal === "delete" && (
          <DeleteProjectModal
            project={project}
            counts={{ incomes: incomes.length, expenses: expenses.length, notes: notes.length, files: files.length }}
            onClose={() => setModal(null)}
            onConfirm={deleteProject}
          />
        )}
        {lightbox && <Lightbox file={lightbox} projectId={id} onClose={() => setLightbox(null)} />}
      </main>
    </AppShell>
  );
}

// ============ Tabs ============
function OverviewTab({ k, project, incomes, expenses, notes, files, timeline }) {
  const remainingCls = k.remaining_status || "";
  const invoicesCount = incomes.length;
  const filesCount = files.length;
  const notesCount = notes.length;

  return (
    <section data-testid="tab-overview">
      <div className="pd-kpi-grid">
        <div className="pd-kpi contract"><small><Coins size={12} /> Contrato</small><b>{money.format(k.contract_value || 0)}</b></div>
        <div className="pd-kpi collected"><small><CheckCircle2 size={12} /> Cobrado</small><b>{money.format(k.collected || 0)}</b></div>
        <div className={`pd-kpi remaining ${remainingCls}`}><small>Por cobrar</small><b>{money.format(k.remaining || 0)}</b>
          <small className="delta">{remainingCls === "paid" ? "✅ Totalmente pagado" : remainingCls === "partial" ? "🟠 Pago parcial" : remainingCls === "pending" ? "🔴 Sin cobrar" : "Sin contrato definido"}</small>
        </div>
        <div className="pd-kpi income"><small><TrendingUp size={12} /> Ingresos</small><b>{money.format(k.income || 0)}</b></div>
        <div className="pd-kpi expense"><small>Egresos</small><b>{money.format(k.expense || 0)}</b></div>
        <div className="pd-kpi profit"><small>Utilidad</small><b style={{ color: (k.profit || 0) >= 0 ? "#10b981" : "#ef4444" }}>{money.format(k.profit || 0)}</b></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 22 }}>
        <StatMini icon={FileText} label="Ingresos" value={invoicesCount} />
        <StatMini icon={Paperclip} label="Archivos" value={filesCount} />
        <StatMini icon={StickyNote} label="Notas" value={notesCount} />
        <StatMini icon={ListChecks} label="% Avance" value={`${k.progress_pct || 0}%`} />
      </div>

      <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: "#fff", borderRadius: 18, padding: 20, border: "1px solid #e2e8f0" }}>
          <h3 style={{ margin: "0 0 12px", font: "900 15px Nunito, sans-serif" }}>Actividad reciente</h3>
          {timeline.length === 0 ? (
            <p style={{ color: "#94a3b8", fontSize: 13 }}>Aún sin actividad.</p>
          ) : (
            <ul className="pd-timeline" style={{ maxHeight: 260, overflowY: "auto" }}>
              {timeline.slice(0, 6).map((ev) => (
                <li key={ev.id} className={tlClass(ev.kind)}>
                  <span className="tl-msg">{ev.message}</span>
                  <small className="tl-when">{new Date(ev.created_at).toLocaleString("es-MX")}</small>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ background: "#fff", borderRadius: 18, padding: 20, border: "1px solid #e2e8f0" }}>
          <h3 style={{ margin: "0 0 12px", font: "900 15px Nunito, sans-serif" }}>Notas fijadas</h3>
          {notes.filter((n) => n.pinned).length === 0 ? (
            <p style={{ color: "#94a3b8", fontSize: 13 }}>Fija tus notas más importantes desde la pestaña Notas.</p>
          ) : (
            notes.filter((n) => n.pinned).slice(0, 3).map((n) => (
              <div key={n.id} style={{ padding: "10px 0", borderBottom: "1px dashed #e2e8f0" }}>
                <b style={{ font: "800 14px Nunito, sans-serif" }}><Pin size={12} /> {n.title}</b>
                <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>{n.description?.slice(0, 100)}{n.description?.length > 100 ? "…" : ""}</p>
              </div>
            ))
          )}
        </div>
      </section>
    </section>
  );
}

function StatMini({ icon: Icon, label, value }) {
  return (
    <div style={{ padding: 14, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ padding: 8, background: "#eef2ff", color: "#4169e1", borderRadius: 10 }}><Icon size={16} /></div>
      <div><small style={{ display: "block", font: "700 11px Nunito, sans-serif", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</small><b style={{ font: "900 18px Nunito, sans-serif" }}>{value}</b></div>
    </div>
  );
}

function tlClass(kind) {
  if (kind === "income_added") return "income";
  if (kind === "expense_added") return "expense";
  if (kind === "note_added") return "note";
  if (kind === "file_uploaded") return "file";
  return "";
}

function FinancialsTab({ incomes, expenses, onAddIncome, onAddExpense, onEditIncome, onEditExpense, onDeleteIncome, onDeleteExpense }) {
  return (
    <section data-testid="tab-financials" style={{ display: "grid", gap: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: 20, border: "1px solid #e2e8f0" }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, font: "900 16px Nunito, sans-serif" }}>Ingresos</h3>
          <button onClick={onAddIncome} className="cash-btn primary" style={{ padding: "6px 12px", fontSize: 13 }}><Plus size={13} /> Añadir</button>
        </header>
        {incomes.length === 0 ? (
          <EmptyMini label="Aún no hay ingresos registrados." />
        ) : (
          <table className="expenses-table" data-testid="project-incomes-table">
            <thead><tr><th>Fecha</th><th>Concepto</th><th>Cliente</th><th>Estado</th><th style={{ textAlign: "right" }}>Monto</th><th></th></tr></thead>
            <tbody>
              {incomes.map((r) => (
                <tr key={r.id} data-testid={`project-income-row-${r.id}`}>
                  <td>{(r.date || "").slice(0, 10)}</td><td>{r.concept}</td><td>{r.client || "—"}</td>
                  <td>{(r.payment_status === "pendiente" || r.payment_status === "por_cobrar") ? <span className="status-badge pending">Por cobrar</span> : <span className="status-badge active">Cobrado</span>}</td>
                  <td style={{ textAlign: "right", color: "#10b981", fontWeight: 800 }}>{money.format(r.amount || 0)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button onClick={() => onEditIncome(r)} className="cash-btn ghost" style={{ padding: "4px 8px", color: "#4169e1", marginRight: 4 }} data-testid={`project-income-edit-${r.id}`}><Pencil size={13} /></button>
                    <button onClick={() => onDeleteIncome(r)} className="cash-btn ghost" style={{ padding: "4px 8px", color: "#ef4444" }} data-testid={`project-income-delete-${r.id}`} title="Eliminar"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: 18, padding: 20, border: "1px solid #e2e8f0" }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, font: "900 16px Nunito, sans-serif" }}>Egresos</h3>
          <button onClick={onAddExpense} className="cash-btn ghost" style={{ padding: "6px 12px", fontSize: 13 }}><Plus size={13} /> Añadir</button>
        </header>
        {expenses.length === 0 ? (
          <EmptyMini label="No hay egresos registrados." />
        ) : (
          <table className="expenses-table" data-testid="project-expenses-table">
            <thead><tr><th>Fecha</th><th>Proveedor</th><th>Categoría</th><th style={{ textAlign: "right" }}>Monto</th><th></th></tr></thead>
            <tbody>
              {expenses.map((r) => (
                <tr key={r.id} data-testid={`project-expense-row-${r.id}`}>
                  <td>{(r.purchase_date || r.date || "").slice(0, 10)}</td><td>{r.merchant}</td><td>{r.category}</td>
                  <td style={{ textAlign: "right", color: "#ef4444", fontWeight: 800 }}>{money.format(r.amount || 0)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button onClick={() => onEditExpense(r)} className="cash-btn ghost" style={{ padding: "4px 8px", color: "#4169e1", marginRight: 4 }} data-testid={`project-expense-edit-${r.id}`}><Pencil size={13} /></button>
                    <button onClick={() => onDeleteExpense(r)} className="cash-btn ghost" style={{ padding: "4px 8px", color: "#ef4444" }} data-testid={`project-expense-delete-${r.id}`} title="Eliminar"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function EmptyMini({ label }) {
  return <p style={{ padding: 24, textAlign: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 12 }}>{label}</p>;
}

function NotesTab({ notes, onAdd, onEdit, onTogglePin, onDelete }) {
  const [search, setSearch] = useState("");
  const filtered = notes.filter((n) => !search || (n.title + " " + (n.description || "") + " " + (n.tags || []).join(" ")).toLowerCase().includes(search.toLowerCase()));

  return (
    <section data-testid="tab-notes">
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <div className="projects-toolbar" style={{ flex: 1, margin: 0 }}>
          <div className="search" style={{ flex: 1 }}>
            <Search size={16} color="#94a3b8" />
            <input placeholder="Buscar en tus notas…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="notes-search" />
          </div>
        </div>
        <button onClick={onAdd} className="cash-btn primary" data-testid="notes-add-btn"><Plus size={14} /> Nueva nota</button>
      </div>

      {filtered.length === 0 ? (
        <div className="pd-empty" data-testid="notes-empty">
          <div className="pd-empty-icon"><StickyNote size={22} /></div>
          <h3>No tienes notas aún.</h3>
          <p>Guarda ideas, decisiones, contactos o pendientes del proyecto en un solo lugar.</p>
          <button onClick={onAdd} className="cash-btn primary"><Plus size={14} /> Crear la primera</button>
        </div>
      ) : (
        <div className="notes-list">
          {filtered.map((n) => (
            <article key={n.id} className={`note-card ${n.pinned ? "pinned" : ""}`} data-testid={`note-${n.id}`}>
              <div className="note-head">
                <h3>{n.pinned && <Pin size={14} color="#f59e0b" />} {n.title}</h3>
                <div className="note-actions">
                  <button onClick={() => onTogglePin(n)} title={n.pinned ? "Desfijar" : "Fijar"} data-testid={`note-pin-${n.id}`}>
                    {n.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                  </button>
                  <button onClick={() => onEdit(n)} title="Editar" data-testid={`note-edit-${n.id}`}><Pencil size={14} /></button>
                  <button onClick={() => onDelete(n)} className="del" title="Eliminar" data-testid={`note-delete-${n.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
              {n.description && <p>{n.description}</p>}
              <div className="note-meta">
                <small>{n.date}</small>
                {(n.tags || []).map((t) => <span key={t} className="note-tag">{t}</span>)}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function FilesTab({ files, onUpload, onDelete, onRename, onOpenImage }) {
  const [dragging, setDragging] = useState(false);
  const [pickCategory, setPickCategory] = useState("other");
  const inputRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer?.files?.length) onUpload(e.dataTransfer.files, pickCategory);
  };

  return (
    <section data-testid="tab-files">
      <div style={{ display: "flex", gap: 12, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <select value={pickCategory} onChange={(e) => setPickCategory(e.target.value)} data-testid="files-category-picker" style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e2e8f0", font: "600 13px Nunito, sans-serif" }}>
          {FILE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>Subir como: {c.label}</option>)}
        </select>
      </div>

      <div
        className={`files-drop ${dragging ? "dragging" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
        data-testid="files-dropzone"
      >
        <Upload size={26} style={{ marginBottom: 6 }} />
        <div style={{ font: "800 15px Nunito, sans-serif" }}>Arrastra archivos aquí o toca para elegir</div>
        <small>Solo <b>PDF, PNG o JPG</b> · hasta 25 MB por archivo · varios a la vez</small>
        <button
          type="button"
          className="files-drop-mobile-btn"
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          data-testid="files-pick-button"
        >
          <Upload size={14} /> Elegir archivos
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
          onChange={(e) => {
            if (e.target.files) onUpload(e.target.files, pickCategory);
            e.target.value = "";
          }}
          style={{ display: "none" }}
          data-testid="files-input"
        />
      </div>

      {files.length === 0 ? (
        <div className="pd-empty" style={{ marginTop: 20 }} data-testid="files-empty">
          <div className="pd-empty-icon"><Paperclip size={22} /></div>
          <h3>Sube tu primer contrato o comprobante.</h3>
          <p>Puedes organizarlos por categoría: contratos, facturas, recibos, fotos, planos y más.</p>
        </div>
      ) : (
        <div className="files-grid">
          {files.map((f) => (
            <article key={f.id} className="file-card" data-testid={`file-${f.id}`}>
              <span className="file-cat">{(FILE_CATEGORIES.find((c) => c.key === f.category) || { label: "Otro" }).label}</span>
              <div className="file-preview" onClick={() => isImage(f.extension) ? onOpenImage(f) : null} style={{ cursor: isImage(f.extension) ? "zoom-in" : "default" }}>
                {isImage(f.extension)
                  ? <img src={`${api.defaults.baseURL}/projects/${window.location.pathname.split('/').pop()}/files/${f.id}/download`} alt={f.name} />
                  : <FileText size={36} />}
              </div>
              <h4>{f.name}</h4>
              <small>{bytesTo(f.size)} · {new Date(f.created_at).toLocaleDateString("es-MX")}</small>
              <small style={{ color: "#94a3b8" }}>por {f.uploaded_by_name || "—"}</small>
              <div className="file-actions">
                {f.extension === "pdf" && (
                  <a href={`${api.defaults.baseURL}/projects/${window.location.pathname.split('/').pop()}/files/${f.id}/download`} target="_blank" rel="noreferrer" data-testid={`file-view-${f.id}`}><FileText size={12} /> Ver</a>
                )}
                <a href={`${api.defaults.baseURL}/projects/${window.location.pathname.split('/').pop()}/files/${f.id}/download`} target="_blank" rel="noreferrer" data-testid={`file-download-${f.id}`}><FileDown size={12} /> Descargar</a>
                <button onClick={() => onRename(f)} data-testid={`file-rename-${f.id}`}><Pencil size={12} /></button>
                <button onClick={() => onDelete(f)} className="del" data-testid={`file-delete-${f.id}`}><Trash2 size={12} /></button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function TimelineTab({ events }) {
  if (events.length === 0) {
    return (
      <section data-testid="tab-timeline">
        <div className="pd-empty" data-testid="timeline-empty">
          <div className="pd-empty-icon"><Timer size={22} /></div>
          <h3>Aún sin actividad.</h3>
          <p>Cuando agregues ingresos, egresos, notas o archivos, aparecerán aquí en orden cronológico.</p>
        </div>
      </section>
    );
  }
  return (
    <section data-testid="tab-timeline">
      <ul className="pd-timeline">
        {events.map((ev) => (
          <li key={ev.id} className={tlClass(ev.kind)} data-testid={`timeline-event-${ev.id}`}>
            <span className="tl-msg">{ev.message}</span>
            <small className="tl-when">{new Date(ev.created_at).toLocaleString("es-MX")}</small>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SettingsTab({ project, onSaved, onInvite }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setForm({
      name: project.name || "", client_name: project.client_name || "", category: project.category || "",
      description: project.description || "", color: project.color || "#4169e1", status: project.status || "active",
      contract_value: project.contract_value || "", budget: project.budget || "",
      start_date: project.start_date || "", due_date: project.due_date || "", end_date: project.end_date || "",
      payment_terms: project.payment_terms || "",
    });
  }, [project]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true); setErr(""); setOk("");
    try {
      const payload = { ...form };
      ["contract_value", "budget"].forEach((k) => { if (payload[k] === "") delete payload[k]; else payload[k] = Number(payload[k]); });
      await api.put(`/projects/${project.id}`, payload, { withCredentials: true });
      setOk("Guardado.");
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || "No pudimos guardar."); }
    finally { setSaving(false); }
  };

  return (
    <section data-testid="tab-settings" style={{ display: "grid", gap: 18 }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }}>
        <h3 style={{ margin: "0 0 14px", font: "900 16px Nunito, sans-serif" }}>Información</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>Nombre<input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="settings-name" /></label>
          <label>Cliente<input value={form.client_name || ""} onChange={(e) => set("client_name", e.target.value)} data-testid="settings-client" /></label>
          <label>Categoría<input value={form.category || ""} onChange={(e) => set("category", e.target.value)} /></label>
          <label>Estatus
            <select value={form.status || "active"} onChange={(e) => set("status", e.target.value)} data-testid="settings-status">
              <option value="active">Activo</option>
              <option value="pending">Pendiente</option>
              <option value="paused">En pausa</option>
              <option value="completed">Completado</option>
              <option value="archived">Archivado</option>
            </select>
          </label>
          <label>Color<input type="color" value={form.color || "#4169e1"} onChange={(e) => set("color", e.target.value)} /></label>
          <label>Términos de pago<input value={form.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} /></label>
        </div>
        <label>Descripción<textarea rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></label>
      </div>

      <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }}>
        <h3 style={{ margin: "0 0 14px", font: "900 16px Nunito, sans-serif" }}>Contrato y presupuesto</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>Valor del contrato<input type="number" step="0.01" value={form.contract_value ?? ""} onChange={(e) => set("contract_value", e.target.value)} data-testid="settings-contract-value" /></label>
          <label>Presupuesto interno<input type="number" step="0.01" value={form.budget ?? ""} onChange={(e) => set("budget", e.target.value)} /></label>
          <label>Fecha inicio<input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></label>
          <label>Vencimiento<input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="settings-due-date" /></label>
          <label>Fecha fin<input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></label>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, font: "900 16px Nunito, sans-serif" }}>Colaboradores ({project.collaborators?.length || 0})</h3>
          <button onClick={onInvite} className="cash-btn primary" style={{ padding: "6px 12px", fontSize: 13 }} data-testid="project-invite-collab"><Plus size={13} /> Invitar</button>
        </header>
        {(project.collaborators || []).length === 0 ? (
          <p style={{ color: "#94a3b8" }}>Aún nadie invitado.</p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {(project.collaborators || []).map((c) => (
              <li key={c.id} style={{ padding: "10px 0", borderBottom: "1px dashed #e2e8f0" }} data-testid={`project-collab-row-${c.id}`}>
                <b style={{ display: "block" }}>{c.name || c.email}</b>
                <small style={{ color: "#64748b" }}>{c.email} · {c.role} · {c.status}</small>
              </li>
            ))}
          </ul>
        )}
      </div>

      {ok && <p style={{ background: "#dcfce7", color: "#166534", padding: 10, borderRadius: 10 }}>{ok}</p>}
      {err && <p className="expenses-error">{err}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={save} disabled={saving} className="cash-btn primary" data-testid="settings-save">
          <Save size={14} /> {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </section>
  );
}

// ============ Modals ============
function DeleteProjectModal({ project, counts, onClose, onConfirm }) {
  const [cascade, setCascade] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const target = (project?.name || "").trim();
  const canConfirm = confirmText.trim().toLowerCase() === target.toLowerCase() && target.length > 0;
  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try { await onConfirm(cascade); } finally { setSubmitting(false); }
  };
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="project-delete-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(520px, 96vw)" }}>
        <header>
          <div>
            <small>ELIMINAR PROYECTO</small>
            <b style={{ color: "#ef4444" }}>Esta acción no se puede deshacer</b>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </header>
        <div style={{ padding: "0 24px 16px" }}>
          <div style={{ display: "flex", gap: 10, padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, marginBottom: 14 }}>
            <AlertTriangle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <b style={{ display: "block", color: "#991b1b", font: "800 14px Nunito, sans-serif" }}>Vas a eliminar "{target}"</b>
              <small style={{ color: "#7f1d1d", display: "block", marginTop: 4 }}>
                Se borrarán {counts.notes} nota(s), {counts.files} archivo(s), toda su actividad y el enlace público (si existe).
              </small>
            </div>
          </div>

          <label style={{ display: "flex", gap: 10, padding: 12, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, cursor: "pointer", alignItems: "flex-start" }}>
            <input
              type="checkbox"
              checked={cascade}
              onChange={(e) => setCascade(e.target.checked)}
              data-testid="project-delete-cascade"
              style={{ marginTop: 3 }}
            />
            <div>
              <b style={{ font: "800 13px Nunito, sans-serif", color: "#92400e" }}>Eliminar también {counts.incomes} ingreso(s) y {counts.expenses} egreso(s) vinculados</b>
              <small style={{ display: "block", marginTop: 4, color: "#78350f" }}>
                Si desmarcas esta opción, los ingresos y egresos permanecerán en tu contabilidad global (Dashboard, Declaraciones) sin la etiqueta del proyecto.
              </small>
            </div>
          </label>

          <label style={{ marginTop: 14, display: "block", font: "700 12px Nunito, sans-serif", color: "#475569" }}>
            Escribe <b style={{ color: "#ef4444" }}>{target}</b> para confirmar
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={target}
              data-testid="project-delete-confirm-input"
              style={{ marginTop: 6, width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 10, font: "600 14px Nunito, sans-serif" }}
            />
          </label>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button
            onClick={submit}
            disabled={!canConfirm || submitting}
            className="cash-btn primary"
            style={{ background: canConfirm ? "#ef4444" : "#fca5a5", borderColor: canConfirm ? "#ef4444" : "#fca5a5" }}
            data-testid="project-delete-confirm"
          >
            <Trash2 size={14} /> {submitting ? "Eliminando…" : "Eliminar proyecto"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteModal({ form, setForm, onClose, onSubmit }) {
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="project-note-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()}>
        <header><div><small>{form.id ? "EDITAR NOTA" : "NUEVA NOTA"}</small><b>Del proyecto</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "0 24px 16px" }}>
          <label>Título<input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="note-title" /></label>
          <label>Descripción<textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="note-description" /></label>
          <label>Tags (separadas por coma)<input value={form.tagsRaw || ""} onChange={(e) => setForm({ ...form, tagsRaw: e.target.value })} placeholder="cliente, urgente, kickoff" data-testid="note-tags" /></label>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700 }}>
            <input type="checkbox" checked={!!form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="note-pinned" /> Fijar arriba
          </label>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button onClick={onSubmit} className="cash-btn primary" disabled={!form.title} data-testid="note-save">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function CollabModal({ form, setForm, onClose, onSubmit }) {
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="project-collab-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()}>
        <header><div><small>INVITAR COLABORADOR</small><b>Se manda invitación por email</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "0 24px 16px" }}>
          <label>Email<input type="email" required value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="project-invite-email" /></label>
          <label>Rol<select value={form.role || "collaborator"} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="project-invite-role">
            <option value="admin">Admin</option>
            <option value="collaborator">Colaborador</option>
            <option value="viewer">Lector</option>
          </select></label>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button onClick={onSubmit} className="cash-btn primary" disabled={!form.email} data-testid="project-invite-send">Enviar invitación</button>
        </div>
      </div>
    </div>
  );
}

function CloseModal({ checklist, onClose, onFinalize }) {
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="project-close-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(520px, 96vw)" }}>
        <header><div><small>CERRAR PROYECTO</small><b>Checklist final</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "0 24px 16px" }}>
          <ul className="academia-checklist" data-testid="project-close-checklist">
            {checklist.checklist.map((c) => (
              <li key={c.key} data-testid={`close-check-${c.key}`}>
                {c.ok ? <CheckCircle2 size={16} style={{ color: "#238a65" }} /> : <XCircle size={16} style={{ color: "#d7565d" }} />}
                <span>{c.label} <em style={{ color: "#64748b", fontSize: 12 }}>· {c.detail}</em></span>
              </li>
            ))}
          </ul>
          <p style={{ marginTop: 14, fontSize: 15 }}><b>Utilidad final:</b> <span style={{ color: checklist.final_profit >= 0 ? "#238a65" : "#d7565d", fontWeight: 900 }}>{money.format(checklist.final_profit)}</span></p>
          <p style={{ fontSize: 13, color: "#64748b" }}>Al finalizar, el proyecto se marca como <b>Completado</b> y sus ingresos/egresos quedan disponibles en la contabilidad global.</p>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button onClick={onFinalize} className="cash-btn primary" data-testid="project-close-finalize">Marcar como completado</button>
        </div>
      </div>
    </div>
  );
}

function Lightbox({ file, projectId, onClose }) {
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="file-lightbox" style={{ background: "rgba(15,23,42,0.85)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "92vw", maxHeight: "92vh" }}>
        <img
          src={`${api.defaults.baseURL}/projects/${projectId}/files/${file.id}/download`}
          alt={file.name}
          style={{ maxWidth: "92vw", maxHeight: "88vh", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
        />
        <div style={{ color: "#fff", textAlign: "center", marginTop: 10, font: "700 14px Nunito, sans-serif" }}>{file.name}</div>
      </div>
    </div>
  );
}

function ShareModal({ projectId, project, onClose, onChanged }) {
  const [token, setToken] = useState(project?.share_token || "");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const url = token ? `${window.location.origin}/p/${token}` : "";

  const enable = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.post(`/projects/${projectId}/share`, {}, { withCredentials: true });
      setToken(r.data.share_token);
      onChanged();
    } catch (e) { setErr(e.response?.data?.detail || "No pudimos generar el enlace."); }
    finally { setLoading(false); }
  };

  const revoke = async () => {
    if (!window.confirm("¿Revocar el enlace público? Ya nadie podrá verlo.")) return;
    setLoading(true); setErr("");
    try {
      await api.delete(`/projects/${projectId}/share`, { withCredentials: true });
      setToken(""); onChanged();
    } catch (e) { setErr(e.response?.data?.detail || "No pudimos revocar."); }
    finally { setLoading(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="share-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(560px, 96vw)" }}>
        <header><div><small>COMPARTIR CON CLIENTE</small><b>Enlace público de solo lectura</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "0 24px 16px" }}>
          <p style={{ color: "#64748b", fontSize: 14, marginTop: 0 }}>
            Genera un enlace público para mostrar el reporte del proyecto (KPIs + movimientos) a tu cliente. Sin dar acceso a tu app y sin exponer notas ni archivos internos.
          </p>
          {!token ? (
            <div className="pd-empty" style={{ margin: 0 }} data-testid="share-empty">
              <div className="pd-empty-icon"><Link2 size={22} /></div>
              <h3>Aún no está compartido.</h3>
              <p>Genera un enlace único; puedes revocarlo cuando quieras.</p>
              <button onClick={enable} disabled={loading} className="cash-btn primary" data-testid="share-enable"><Link2 size={14} /> {loading ? "Generando…" : "Generar enlace"}</button>
            </div>
          ) : (
            <>
              <label style={{ font: "700 12px Nunito, sans-serif", color: "#475569" }}>Enlace público</label>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <input value={url} readOnly onClick={(e) => e.target.select()} data-testid="share-url" style={{ flex: 1, padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 10, font: "500 13px monospace" }} />
                <button onClick={copy} className="cash-btn primary" data-testid="share-copy"><Copy size={14} /> {copied ? "¡Copiado!" : "Copiar"}</button>
              </div>
              <p style={{ background: "#dcfce7", color: "#166534", padding: 10, borderRadius: 10, fontSize: 13, marginTop: 12 }}>
                ✅ Enlace activo. Compártelo por correo o WhatsApp.
              </p>
              <a href={url} target="_blank" rel="noreferrer" data-testid="share-preview" style={{ display: "inline-block", marginTop: 10, color: "#4169e1", font: "700 13px Nunito, sans-serif" }}>Ver como cliente ↗</a>
            </>
          )}
          {err && <p className="expenses-error" style={{ marginTop: 10 }}>{err}</p>}
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cerrar</button>
          {token && <button onClick={revoke} disabled={loading} className="cash-btn ghost" style={{ color: "#ef4444" }} data-testid="share-revoke"><Trash2 size={14} /> Revocar</button>}
        </div>
      </div>
    </div>
  );
}
